/**
 * DICOMweb Browser UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Полный UI для подключения к PACS серверам через DICOMweb:
 *   ✓ Выбор preset-сервера (Orthanc, dcm4chee, Google, Custom)
 *   ✓ Тест соединения
 *   ✓ Поиск исследований (QIDO-RS)
 *   ✓ Просмотр серий
 *   ✓ Загрузка серии в Viewer (WADO-RS → Cornerstone imageIds)
 *   ✓ История последних серверов (localStorage)
 *   ✓ Авторизация: Bearer / Basic / None
 */

import { DICOMwebClient, SERVER_PRESETS, createClient, getClient, configureCornerstoneAuth } from '../io/dicomWebClient.js';
import { dicomImageLoader } from '../core/init.js';
import { showToast, escapeHtml } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


const $ = (id) => document.getElementById(id);

let _ctx = null;       // shared context from uiManager
let _studies = [];      // last search results
let _series = [];       // series of selected study

// ══════════════════════════════════════════════════════════════
// PUBLIC
// ══════════════════════════════════════════════════════════════

/**
 * Инициализация DICOMweb UI.
 * @param {Object} ctx — shared context (renderSeriesList, loadSeriesIntoViewport, etc.)
 */
export function initDICOMwebUI(ctx) {
  _ctx = ctx;
  _injectModal();
  _bindEvents();
  _loadRecentServers();
}

// ══════════════════════════════════════════════════════════════
// MODAL HTML
// ══════════════════════════════════════════════════════════════

function _injectModal() {
  // Заменяем содержимое существующего modal-wado или создаём новый
  let modal = $('modal-wado');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-wado';
    modal.className = 'modal-overlay';
    modal.style.cssText = 'display:none;position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:100000;align-items:center;justify-content:center;';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="dweb-modal" style="
      background:#13132a;border-radius:12px;border:1px solid #2a2a40;
      width:720px;max-width:95vw;max-height:90vh;overflow-y:auto;
      padding:24px;color:#e0e0e0;font-family:system-ui,sans-serif;
      box-shadow:0 12px 48px rgba(0,0,0,0.6);
    ">
      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
        <h2 style="margin:0;font-size:18px;color:#fff;">
          🌐 DICOMweb — Подключение к PACS
        </h2>
        <button id="dweb-close" aria-label="Закрыть" style="background:none;border:none;color:#888;font-size:24px;cursor:pointer;padding:4px 8px;">&times;</button>
      </div>

      <!-- Server Preset -->
      <div style="margin-bottom:12px;">
        <label for="dweb-preset" style="font-size:12px;color:#888;display:block;margin-bottom:4px;">Тип сервера</label>
        <select id="dweb-preset" style="
          width:100%;padding:8px 12px;background:#1a1a2e;border:1px solid #333;
          border-radius:6px;color:#fff;font-size:14px;
        ">
          <option value="orthanc">Orthanc</option>
          <option value="dcm4chee">dcm4chee-arc</option>
          <option value="google">Google Healthcare API</option>
          <option value="ohif">OHIF Backend</option>
          <option value="custom" selected>Другой сервер</option>
        </select>
        <div id="dweb-preset-hint" style="font-size:11px;color:#666;margin-top:2px;"></div>
      </div>

      <!-- URL -->
      <div style="margin-bottom:12px;">
        <label for="dweb-url" style="font-size:12px;color:#888;display:block;margin-bottom:4px;">DICOMweb URL</label>
        <input id="dweb-url" type="text" placeholder="https://your-server/dicom-web"
          style="width:100%;padding:8px 12px;background:#1a1a2e;border:1px solid #333;
                 border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box;" />
      </div>

      <!-- Auth row -->
      <div style="display:flex;gap:8px;margin-bottom:12px;">
        <div style="flex:0 0 140px;">
          <label for="dweb-auth" style="font-size:12px;color:#888;display:block;margin-bottom:4px;">Авторизация</label>
          <select id="dweb-auth" style="width:100%;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;">
            <option value="none">Без авторизации</option>
            <option value="basic">Basic Auth</option>
            <option value="bearer">Bearer Token</option>
          </select>
        </div>
        <div id="dweb-auth-fields" style="flex:1;display:none;gap:8px;"></div>
      </div>

      <!-- Buttons row -->
      <div style="display:flex;gap:8px;margin-bottom:16px;">
        <button id="dweb-test" style="
          padding:8px 16px;background:#2563eb;border:none;color:#fff;
          border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;
        ">🔌 Тест соединения</button>
        <button id="dweb-connect" style="
          padding:8px 16px;background:#059669;border:none;color:#fff;
          border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;
        ">Подключить</button>
        <span id="dweb-status" role="status" aria-live="polite" style="display:flex;align-items:center;font-size:12px;color:#888;"></span>
      </div>

      <hr style="border:none;border-top:1px solid #2a2a40;margin:16px 0;" />

      <!-- Search -->
      <div id="dweb-search-section" style="display:none;">
        <h3 style="font-size:15px;margin:0 0 12px;color:#fff;">🔍 Поиск исследований</h3>
        <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
          <input id="dweb-q-name" type="text" placeholder="Имя пациента" aria-label="Имя пациента"
            style="flex:1;min-width:140px;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
          <input id="dweb-q-id" type="text" placeholder="ID пациента" aria-label="ID пациента"
            style="flex:0 0 120px;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
          <select id="dweb-q-mod" aria-label="Модальность" style="flex:0 0 80px;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;">
            <option value="">Все</option>
            <option value="CT">CT</option>
            <option value="MR">MR</option>
            <option value="US">US</option>
            <option value="XA">XA</option>
            <option value="CR">CR</option>
            <option value="DX">DX</option>
            <option value="NM">NM</option>
            <option value="PT">PT</option>
          </select>
          <input id="dweb-q-date" type="text" placeholder="YYYYMMDD" aria-label="Дата исследования"
            style="flex:0 0 110px;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
          <button id="dweb-search" style="
            padding:8px 16px;background:#7c3aed;border:none;color:#fff;
            border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;
          ">Найти</button>
        </div>

        <!-- Results table -->
        <div id="dweb-results" role="region" aria-label="Результаты поиска" style="max-height:300px;overflow-y:auto;"></div>

        <!-- Series panel -->
        <div id="dweb-series-panel" style="display:none;margin-top:12px;">
          <h4 style="font-size:14px;margin:0 0 8px;color:#fff;" id="dweb-series-title">Серии</h4>
          <div id="dweb-series-list" style="max-height:200px;overflow-y:auto;"></div>
        </div>
      </div>

      <!-- Recent servers -->
      <div id="dweb-recent" style="margin-top:12px;"></div>
    </div>
  `;
}

// ══════════════════════════════════════════════════════════════
// EVENT BINDINGS
// ══════════════════════════════════════════════════════════════

function _bindEvents() {
  // Close
  $('dweb-close')?.addEventListener('click', () => {
    const m = $('modal-wado');
    if (m) m.style.display = 'none';
  }, { signal: _ac.signal });

  // Preset change
  $('dweb-preset')?.addEventListener('change', (e) => {
    const preset = SERVER_PRESETS[e.target.value];
    if (preset) {
      if (preset.defaultUrl) $('dweb-url').value = preset.defaultUrl;
      if (preset.authType !== undefined) $('dweb-auth').value = preset.authType;
      $('dweb-preset-hint').textContent = preset.notes || '';
      _toggleAuthFields();
    }
  }, { signal: _ac.signal });

  // Auth change
  $('dweb-auth')?.addEventListener('change', _toggleAuthFields, { signal: _ac.signal });

  // Test
  $('dweb-test')?.addEventListener('click', _testConnection, { signal: _ac.signal });

  // Connect
  $('dweb-connect')?.addEventListener('click', _connect, { signal: _ac.signal });

  // Search
  $('dweb-search')?.addEventListener('click', _search, { signal: _ac.signal });

  // Enter to search
  for (const id of ['dweb-q-name', 'dweb-q-id', 'dweb-q-date']) {
    $(id)?.addEventListener('keydown', (e) => { if (e.key === 'Enter') _search(); }, { signal: _ac.signal });
  }

  // Init auth fields visibility
  _toggleAuthFields();
}

function _toggleAuthFields() {
  const authType = $('dweb-auth')?.value || 'none';
  const container = $('dweb-auth-fields');
  if (!container) return;

  if (authType === 'basic') {
    container.style.display = 'flex';
    container.innerHTML = `
      <input id="dweb-username" type="text" placeholder="Логин" aria-label="Логин" style="flex:1;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
      <input id="dweb-password" type="password" placeholder="Пароль" aria-label="Пароль" style="flex:1;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
    `;
  } else if (authType === 'bearer') {
    container.style.display = 'flex';
    container.innerHTML = `
      <input id="dweb-token" type="password" placeholder="Bearer Token / API Key" aria-label="Токен авторизации" style="flex:1;padding:8px;background:#1a1a2e;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;" />
    `;
  } else {
    container.style.display = 'none';
    container.innerHTML = '';
  }
}

function _getConfig() {
  const url = $('dweb-url')?.value?.trim();
  if (!url) { showToast('Укажите DICOMweb URL', 'warning'); return null; }

  const authType = $('dweb-auth')?.value || 'none';
  const config = { url, authType };

  if (authType === 'basic') {
    config.username = $('dweb-username')?.value || '';
    config.password = $('dweb-password')?.value || '';
  } else if (authType === 'bearer') {
    config.authToken = $('dweb-token')?.value || '';
  }

  const preset = $('dweb-preset')?.value;
  if (preset && SERVER_PRESETS[preset]) {
    config.qidoPrefix = SERVER_PRESETS[preset].qidoPrefix || '';
    config.wadoPrefix = SERVER_PRESETS[preset].wadoPrefix || '';
  }

  return config;
}

// ══════════════════════════════════════════════════════════════
// ACTIONS
// ══════════════════════════════════════════════════════════════

async function _testConnection() {
  const config = _getConfig();
  if (!config) return;

  const status = $('dweb-status');
  status.textContent = '⏳ Проверка...';
  status.style.color = '#888';

  try {
    const client = new DICOMwebClient(config);
    const result = await client.testConnection();

    if (result.ok) {
      status.innerHTML = `<span style="color:#10b981;">✅ Подключено</span>` +
        (result.serverType !== 'unknown' ? ` <span style="color:#666;">(${result.serverType})</span>` : '');
    } else {
      status.innerHTML = `<span style="color:#ef4444;">❌ ${escapeHtml(result.error || 'Нет ответа')}</span>`;
    }
  } catch (e) {
    status.innerHTML = `<span style="color:#ef4444;">❌ ${escapeHtml(e.message)}</span>`;
  }
}

async function _connect() {
  const config = _getConfig();
  if (!config) return;

  try {
    const client = createClient(config);
    const result = await client.testConnection();

    if (!result.ok) {
      showToast(`❌ Не удалось подключиться: ${result.error}`, 'error');
      return;
    }

    // Configure Cornerstone auth
    configureCornerstoneAuth(dicomImageLoader);

    // Show search UI
    const section = $('dweb-search-section');
    if (section) section.style.display = 'block';

    // Save to recent
    _saveRecentServer(config);

    showToast(`✅ DICOMweb подключен (${result.serverType})`, 'success');
    $('dweb-status').innerHTML = `<span style="color:#10b981;">✅ Подключено</span>`;

  } catch (e) {
    showToast(`❌ Ошибка: ${e.message}`, 'error');
  }
}

async function _search() {
  const client = getClient();
  if (!client) { showToast('Сначала подключитесь к серверу', 'warning'); return; }

  const params = {};
  const name = $('dweb-q-name')?.value?.trim();
  const id = $('dweb-q-id')?.value?.trim();
  const mod = $('dweb-q-mod')?.value;
  const date = $('dweb-q-date')?.value?.trim();

  if (name) params.PatientName = name.includes('*') ? name : name + '*';
  if (id) params.PatientID = id;
  if (mod) params.ModalitiesInStudy = mod;
  if (date) params.StudyDate = date;

  const resultsDiv = $('dweb-results');
  resultsDiv.innerHTML = '<div style="text-align:center;padding:16px;color:#888;">⏳ Поиск...</div>';

  try {
    _studies = await client.searchStudies(params);

    if (!_studies.length) {
      resultsDiv.innerHTML = '<div style="text-align:center;padding:16px;color:#888;">Ничего не найдено</div>';
      return;
    }

    _renderStudyTable(_studies);

  } catch (e) {
    resultsDiv.innerHTML = `<div style="text-align:center;padding:16px;color:#ef4444;">❌ ${escapeHtml(e.message)}</div>`;
  }
}

function _renderStudyTable(studies) {
  const resultsDiv = $('dweb-results');
  let html = `
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr style="background:#1a1a2e;color:#888;">
          <th style="padding:6px 8px;text-align:left;">Пациент</th>
          <th style="padding:6px 8px;text-align:left;">Дата</th>
          <th style="padding:6px 8px;text-align:left;">Описание</th>
          <th style="padding:6px 8px;text-align:center;">Мод.</th>
          <th style="padding:6px 8px;text-align:center;">Серий</th>
          <th style="padding:6px 8px;"></th>
        </tr>
      </thead>
      <tbody>
  `;

  for (let i = 0; i < studies.length; i++) {
    const s = studies[i];
    const dateFormatted = s.studyDate ? `${s.studyDate.slice(0,4)}-${s.studyDate.slice(4,6)}-${s.studyDate.slice(6,8)}` : '';
    html += `
      <tr style="border-bottom:1px solid #222;cursor:pointer;" class="dweb-study-row" data-idx="${i}">
        <td style="padding:6px 8px;color:#e0e0e0;">${escapeHtml(s.patientName || 'Unknown')}</td>
        <td style="padding:6px 8px;color:#999;">${dateFormatted}</td>
        <td style="padding:6px 8px;color:#999;">${escapeHtml((s.studyDescription || '').slice(0, 40))}</td>
        <td style="padding:6px 8px;text-align:center;color:#7c3aed;font-weight:600;">${escapeHtml((s.modalitiesInStudy || []).join('/'))}</td>
        <td style="padding:6px 8px;text-align:center;color:#999;">${s.numberOfSeries || '?'}</td>
        <td style="padding:6px 8px;">
          <button class="dweb-view-series" data-idx="${i}" style="
            padding:4px 10px;background:#2563eb;border:none;color:#fff;
            border-radius:4px;cursor:pointer;font-size:11px;
          ">Серии →</button>
        </td>
      </tr>
    `;
  }

  html += '</tbody></table>';
  resultsDiv.innerHTML = html;

  // Bind view series buttons
  resultsDiv.querySelectorAll('.dweb-view-series').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.dataset.idx);
      _loadSeriesList(idx);
    }, { signal: _ac.signal });
  });
}

async function _loadSeriesList(studyIdx) {
  const client = getClient();
  if (!client) return;

  const study = _studies[studyIdx];
  if (!study) return;

  const panel = $('dweb-series-panel');
  const listDiv = $('dweb-series-list');
  const title = $('dweb-series-title');

  panel.style.display = 'block';
  title.textContent = `Серии — ${study.patientName || 'Unknown'}`;
  listDiv.innerHTML = '<div style="text-align:center;padding:12px;color:#888;">⏳ Загрузка серий...</div>';

  try {
    _series = await client.searchSeries(study.studyInstanceUID);

    if (!_series.length) {
      listDiv.innerHTML = '<div style="padding:12px;color:#888;">Серии не найдены</div>';
      return;
    }

    let html = '';
    for (let i = 0; i < _series.length; i++) {
      const s = _series[i];
      html += `
        <div style="display:flex;align-items:center;justify-content:space-between;
                    padding:8px 12px;border-bottom:1px solid #222;" class="dweb-series-item">
          <div>
            <span style="color:#7c3aed;font-weight:600;font-size:13px;">${escapeHtml(s.modality || '?')}</span>
            <span style="color:#e0e0e0;margin-left:8px;font-size:13px;">${escapeHtml(s.seriesDescription || `Серия #${s.seriesNumber}`)}</span>
            <span style="color:#666;margin-left:8px;font-size:11px;">${s.numberOfInstances || '?'} изобр.</span>
          </div>
          <button class="dweb-load-series" data-study="${study.studyInstanceUID}" data-series="${s.seriesInstanceUID}" style="
            padding:6px 14px;background:#059669;border:none;color:#fff;
            border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;
          ">📥 Загрузить</button>
        </div>
      `;
    }
    listDiv.innerHTML = html;

    // Bind load buttons
    listDiv.querySelectorAll('.dweb-load-series').forEach(btn => {
      btn.addEventListener('click', () => {
        const studyUID = btn.dataset.study;
        const seriesUID = btn.dataset.series;
        _loadSeriesIntoViewer(studyUID, seriesUID, btn);
      }, { signal: _ac.signal });
    });

  } catch (e) {
    listDiv.innerHTML = `<div style="padding:12px;color:#ef4444;">❌ ${escapeHtml(e.message)}</div>`;
  }
}

async function _loadSeriesIntoViewer(studyUID, seriesUID, btn) {
  const client = getClient();
  if (!client || !_ctx) return;

  btn.disabled = true;
  btn.textContent = '⏳ Загрузка...';

  try {
    // Получаем imageIds через WADO-RS
    const imageIds = await client.retrieveSeriesAsImageIds(studyUID, seriesUID);

    if (!imageIds.length) {
      showToast('❌ Серия пуста', 'error');
      btn.disabled = false;
      btn.textContent = '📥 Загрузить';
      return;
    }

    // Закрываем модалку
    const modal = $('modal-wado');
    if (modal) modal.style.display = 'none';

    // Загружаем в viewer через Cornerstone wadors imageIds
    showToast(`🌐 Загрузка ${imageIds.length} изображений с PACS...`, 'info', 3000);

    // Используем viewport напрямую — imageIds уже wadors: схема
    const viewport = await import('../core/viewport.js');
    if (viewport.loadImageIds) {
      await viewport.loadImageIds(imageIds, seriesUID);
    } else if (_ctx.loadWadoRsImageIds) {
      await _ctx.loadWadoRsImageIds(imageIds, seriesUID);
    } else {
      // Fallback: загрузить как файлы
      _ctx.showLoadingOverlay('Загрузка с PACS...', `${imageIds.length} изображений`, 0);
      const files = await client.retrieveSeriesAsFiles(studyUID, seriesUID, (n, t) => {
        _ctx.updateLoadingOverlay(Math.round(n/t*100), 'Загрузка с PACS...', `${n}/${t}`);
      });
      _ctx.hideLoadingOverlay();

      if (files.length) {
        // Используем стандартный pipeline
        const { handleFiles } = await import('./fileOps.js');
        await handleFiles(files);
      }
    }

    showToast(`✅ Серия загружена (${imageIds.length} изобр.)`, 'success');

  } catch (e) {
    showToast(`❌ Ошибка загрузки: ${e.message}`, 'error');
    log.error('[DICOMweb UI]', e);
  } finally {
    btn.disabled = false;
    btn.textContent = '📥 Загрузить';
  }
}

// ══════════════════════════════════════════════════════════════
// RECENT SERVERS (localStorage)
// ══════════════════════════════════════════════════════════════

const STORAGE_KEY = 'mikhmel-dicomweb-recent';
const MAX_RECENT = 5;

function _saveRecentServer(config) {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const entry = { url: config.url, authType: config.authType, timestamp: Date.now() };
    // Deduplicate
    const filtered = list.filter(e => e.url !== config.url);
    filtered.unshift(entry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered.slice(0, MAX_RECENT)));
    _renderRecent();
  } catch (_) { console.debug('[DicomWebUI]', _.message); }
}

function _loadRecentServers() {
  _renderRecent();
}

function _renderRecent() {
  const div = $('dweb-recent');
  if (!div) return;

  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!list.length) { div.innerHTML = ''; return; }

    let html = '<div style="font-size:12px;color:#666;margin-bottom:4px;">Недавние серверы:</div>';
    for (const entry of list) {
      html += `
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <button class="dweb-recent-btn" data-url="${escapeHtml(entry.url)}" data-auth="${entry.authType || 'none'}" style="
            flex:1;text-align:left;padding:6px 10px;background:#1a1a2e;border:1px solid #2a2a40;
            border-radius:4px;color:#999;font-size:11px;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          ">${escapeHtml(entry.url)}</button>
        </div>
      `;
    }
    div.innerHTML = html;

    div.querySelectorAll('.dweb-recent-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        $('dweb-url').value = btn.dataset.url;
        $('dweb-auth').value = btn.dataset.auth;
        _toggleAuthFields();
      }, { signal: _ac.signal });
    });
  } catch (_) { console.debug('[DicomWebUI]', _.message); }
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE: Server Health Check
// ══════════════════════════════════════════════════════════════

export async function checkServerHealth() {
  const client = getClient();
  if (!client) { showToast('⚠️ Сервер не подключён', 'warning'); return { ok: false }; }

  const t0 = performance.now();
  try {
    const studies = await client.searchStudies({ limit: 1 });
    const latency = Math.round(performance.now() - t0);
    showToast(`✅ PACS ОК (${latency}ms) — ${studies.length > 0 ? 'QIDO работает' : 'пустой ответ'}`, 'success', 4000);
    return { ok: true, latencyMs: latency };
  } catch (e) {
    showToast(`❌ PACS недоступен: ${e.message}`, 'error', 4000);
    return { ok: false, latencyMs: Math.round(performance.now() - t0) };
  }
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE: Export Study Metadata
// ══════════════════════════════════════════════════════════════

export function exportStudiesJSON() {
  if (!_studies.length) { showToast('⚠️ Сначала выполните поиск', 'warning'); return; }
  const json = JSON.stringify(_studies, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `dicomweb_studies_${Date.now()}.json`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast(`📤 Экспортировано ${_studies.length} исследований`, 'success');
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE: Quick Connect
// ══════════════════════════════════════════════════════════════

export function quickConnect(presetName, customUrl) {
  const preset = SERVER_PRESETS[presetName];
  const url = customUrl || preset?.defaultUrl || '';
  const urlEl = $('dweb-url');
  const presetEl = $('dweb-preset');
  if (urlEl) urlEl.value = url;
  if (presetEl) presetEl.value = presetName || 'custom';
  const modal = $('modal-wado');
  if (modal) modal.style.display = 'flex';
  const connectBtn = $('dweb-connect');
  if (connectBtn) setTimeout(() => connectBtn.click(), 300);
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Connection history, favorites, batch query
// ══════════════════════════════════════════════════════════════

const DWEB_HISTORY_KEY = 'mikhmel_dicomweb_history';

/**
 * Get DICOMweb connection history
 * @returns {Array}
 */
export function getConnectionHistory() {
  try { return JSON.parse(localStorage.getItem(DWEB_HISTORY_KEY) || '[]'); } catch { return []; }
}

/**
 * Save current connection to history
 * @param {string} url
 * @param {string} preset
 */
export function saveConnectionToHistory(url, preset) {
  const history = getConnectionHistory();
  history.unshift({ url, preset, timestamp: Date.now() });
  if (history.length > 20) history.length = 20;
  try { localStorage.setItem(DWEB_HISTORY_KEY, JSON.stringify(history)); } catch { /* non-critical */ }
}

/**
 * Clear connection history
 */
export function clearConnectionHistory() {
  try { localStorage.removeItem(DWEB_HISTORY_KEY); } catch { /* localStorage may be unavailable (private mode) */}
}

/**
 * Quick-test DICOMweb URL (QIDO-RS ping)
 * @param {string} url
 * @returns {Promise<{ok:boolean, status:number, time:number}>}
 */
export async function testDICOMwebConnection(url) {
  const start = performance.now();
  try {
    const resp = await fetch(`${url}/studies?limit=1`, { method: 'GET', signal: AbortSignal.timeout(5000) });
    return { ok: resp.ok, status: resp.status, time: Math.round(performance.now() - start) };
  } catch (e) {
    return { ok: false, status: 0, time: Math.round(performance.now() - start), error: e.message };
  }
}

/**
 * Get count of studies on server
 * @param {string} url
 * @returns {Promise<number>}
 */
export async function getStudyCount(url) {
  try {
    const resp = await fetch(`${url}/studies?limit=0&includefield=00201209`, { signal: AbortSignal.timeout(10000) });
    if (!resp.ok) return -1;
    const data = await resp.json();
    return Array.isArray(data) ? data.length : -1;
  } catch { return -1; }
}

// [FIX] Cleanup all event listeners
export function destroyDicomWebUI() {
  _ac.abort();
  _ac = new AbortController();
}
